"use client";

import { useState, useEffect } from "react";
import { ShieldCheck, FileCheck2, Lock, HeartPulse, CreditCard, Building2, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Certifications() {
  const [isZh, setIsZh] = useState(false);
  useEffect(() => {
    if (typeof window !== "undefined") {
      setIsZh(localStorage.getItem("elite-language") === "zh-Hant");
    }
  }, []);
  const header = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const grid = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const certifications = [{
    icon: ShieldCheck,
    name: isZh ? "CCC 認證" : "CCC Certification",
    description: isZh ? "進入中國市場消費品的強制性安全認證，確保全面符合國家法規標準。" : "Mandatory safety certification for consumer goods entering the Chinese market, ensuring full regulatory compliance.",
    audit: isZh ? "有效 / 2024" : "VALID / 2024"
  }, {
    icon: FileCheck2,
    name: "G-Mark",
    description: isZh ? "適用於玩具及消費品的海灣符合性標誌，完全符合海灣阿拉伯國家合作委員會的安全法規。" : "Gulf conformity tracking for toys and consumer products, meeting the safety standards of the Gulf Cooperation Council.",
    audit: isZh ? "有效 / 2024" : "VALID / 2024"
  }, {
    icon: Lock,
    name: "ISO 9001",
    description: isZh ? "品質管理體系國際標準，確保所有生產線維持一貫卓越的產品質素。" : "International standard for quality management systems, ensuring consistent product excellence across all production lines.",
    audit: isZh ? "有效 / 2024" : "VALID / 2024"
  }, {
    icon: HeartPulse,
    name: "ASTM F963",
    description: isZh ? "美國玩具安全消費者標準規範，就物理與機械危險進行嚴謹檢測。" : "Standard consumer safety specification for toy safety, ensuring rigorous testing for physical and mechanical hazards.",
    audit: isZh ? "有效 / 2024" : "VALID / 2024"
  }, {
    icon: CreditCard,
    name: isZh ? "EN71 合規標準" : "EN71 Compliance",
    description: isZh ? "歐洲玩具安全標準，涵蓋化學、物理及機械特性，以支援全球分銷。" : "European standard for toy safety, covering chemical, physical, and mechanical properties for international distribution.",
    audit: isZh ? "有效 / 2024" : "VALID / 2024"
  }, {
    icon: Building2,
    name: isZh ? "ICTI 關愛玩具責任製造" : "ICTI Ethical Toy",
    description: isZh ? "全球道德生產標準，確保我們生產設施落實公平勞工保障與安全工作環境。" : "Global standard for ethical manufacturing, ensuring fair labor practices and safe working conditions in our facilities.",
    audit: isZh ? "有效 / 2024" : "VALID / 2024"
  }];
  return <section data-section-id="11035" id="market-certifications" className="bg-background text-foreground py-28 md:py-36 px-6 md:px-18">
      <div className="max-w-[1320px] mx-auto">
        <div ref={header.ref} className={`max-w-[760px] mx-auto text-center transition-all duration-500 ease-out ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`} style={{
        transitionTimingFunction: "cubic-bezier(0.16, 1, 0.3, 1)"
      }}>
          <p className="font-mono text-xs uppercase tracking-[0.12em] text-muted-foreground mb-6">
            {isZh ? "法規 / 行業標準" : "REGULATORY / STANDARDS"}
          </p>
          <h2 className="font-default font-semibold text-foreground text-[clamp(2rem,3.6vw,3.25rem)] leading-[1.04] tracking-tight mb-6">
            {isZh ? <>
                全球市場<em className="italic">合規認證。</em>
              </> : <>
                Global market <em className="italic">certifications.</em>
              </>}
          </h2>
          <p className="font-default text-base md:text-[1.0625rem] leading-[1.55] text-muted-foreground">
            {isZh ? "Elite Group Corporation Limited 恪守嚴格的合規標準，確保我們的產品符合所服務各個市場的法定要求，涵蓋中國、海灣阿拉伯國家及全球主要地區。" : "Elite Group Corporation Limited maintains rigorous compliance standards to ensure our products meet the legal requirements of every market we serve, from China to the Gulf States and beyond."}
          </p>
        </div>

        <div className="w-full h-px bg-border mt-16 md:mt-20" />

        <div ref={grid.ref} className={`grid grid-cols-2 md:grid-cols-3 transition-all duration-500 ease-out ${grid.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`} style={{
        transitionTimingFunction: "cubic-bezier(0.16, 1, 0.3, 1)"
      }}>
          {certifications.map((cert, i) => {
          const Icon = cert.icon;
          const isRightEdgeMobile = i % 2 === 1;
          const isRightEdgeDesktop = i % 3 === 2;
          return <div key={i} data-index={i} className={`group p-6 md:p-8 border-b border-border transition-colors duration-200 ${isRightEdgeMobile ? "" : "border-r"} md:border-r ${isRightEdgeDesktop ? "md:border-r-0" : ""} hover:border-primary`}>
                <Icon className="size-12 stroke-[1.5] text-muted-foreground mb-6 transition-colors duration-200 group-hover:text-primary" aria-hidden="true" />
                <h3 className="font-default font-medium text-base text-foreground mb-3">
                  {cert.name}
                </h3>
                <p className="font-default text-sm leading-[1.55] text-muted-foreground mb-8">
                  {cert.description}
                </p>
                <div className="flex items-center justify-between pt-6 border-t border-border">
                  <span className="font-mono text-xs uppercase tracking-[0.02em] text-muted-foreground">
                    {cert.audit}
                  </span>
                  <Link to="#" className="font-default text-sm text-foreground underline underline-offset-4 decoration-border hover:decoration-primary transition-colors duration-200 inline-flex items-center gap-1.5">
                    <span>{isZh ? "驗證狀態" : "Verify status"}</span>
                    <ArrowRight className="size-3.5" aria-hidden="true" />
                  </Link>
                </div>
              </div>;
        })}
        </div>
      </div>
    </section>;
}